import _ from 'lodash';

import { ensureArray } from '@zapier/common-utils';
import resourceNames from './resourceNames';
import { selectErrorOfType, selectErrorMessages } from './utils';

/**
 * This function was moved over from a separate file because TS had an issue
 * building it as CommonJS. I think it has something to do with the scoped class
 * Used in the file but honestly couldn't figure it out.
 * See this PR https://github.com/zapier/zapier/pull/45656
 *
 * @type {function(string, any): any}
 * */
export function createErrorType(name, options) {
  // A summary is the description of the error. No child error messages or detail.
  const errorSummary = error => {
    if (typeof options.summary === 'string') {
      return options.summary;
    }

    if (typeof options.summary === 'function') {
      return options.summary(error);
    }

    if (error.summary) {
      return error.summary;
    } // No summary here, sorry.

    return '';
  };

  // Child error messages of this error.
  const errorErrors = function (error) {
    if (typeof options.errors === 'function') {
      return options.errors(error);
    }

    if (_.isArray(error.errors)) {
      return error.errors;
    }

    if (error.responseText) {
      try {
        const response = JSON.parse(error.responseText);

        if (response.errors) {
          return ensureArray(response.errors);
        }

        // If no errors property, return an empty array, *not* undefined, so callers won't blow up on errors.length
        return [];
      } catch (e) {
        return [];
      }
    }

    return [];
  };

  // Try to make a good message for logging.
  const errorMessage = error => {
    const errors = errorErrors(error);
    const summary = errorSummary(error);

    return errors.length === 0
      ? summary
      : `${summary} (${selectErrorMessages(errors).join(', ')})`;
  };

  const hasFeature = (featureFlag, error) => {
    if (_.isFunction(options[featureFlag])) {
      return !!options[featureFlag](error);
    }

    return !!options[featureFlag];
  };

  const getFeature = (feature, error) => {
    if (_.isFunction(options[feature])) {
      return options[feature](error);
    }

    return '';
  };
  // An error has extra detail beyond the summary and child messages.
  const errorHasDetail = _.partial(hasFeature, 'hasDetail');
  // Extra detail beyond the summary and child messages. Detail is additional information
  // that is not shown to the user by default and is not part of the message.
  const errorDetail = _.partial(getFeature, 'detail');
  // a help link the user can click on for more info
  const errorHasHelpLink = _.partial(hasFeature, 'hasHelpLink');
  const errorHelpLink = _.partial(getFeature, 'helpLink');
  const errorIsPermament = _.partial(hasFeature, 'isPermanent');

  class CustomError extends Error {
    constructor(detail) {
      super();
      const attrs = { name };
      if (_.isFunction(options.assign)) {
        _.extend(attrs, options.assign(detail));
      }
      _.extend(this, attrs);
      if (!this.errors) {
        _.extend(this, { errors: errorErrors(this) });
      }
      this.message = errorMessage(this);
      this.name = name;

      // Use a try/catch block to capture the stack trace. Capturing the stack trace here is
      // necessary, otherwise we will get the stack trace at the time the new error class was created,
      // rather than when it is instantiated.  We add `message` and `name` so that the stack trace
      // string will match our current error class.
      try {
        throw new Error(this.message);
      } catch (err) {
        err.name = attrs.name;
        this.stack = err.stack;
      }
    }
    static summary(error) {
      return errorSummary(error);
    }
    static hasDetail(error) {
      return errorHasDetail(error);
    }
    static detail(error) {
      return errorDetail(error);
    }
    static errors(error) {
      return errorErrors(error);
    }
    static hasHelpLink(error) {
      return errorHasHelpLink(error);
    }
    static helpLink(error) {
      return errorHelpLink(error);
    }
    static isPermanent(error) {
      return errorIsPermament(error);
    }
    static inspect(error) {
      return error.message
        ? '[' + name + ': ' + error.message + ']'
        : '[' + name + ']';
    }
  }

  return CustomError;
}

/** @type {function(any): any} */
function createErrorTypes(errorTypes) {
  return _.fromPairs(
    Object.keys(errorTypes).map(name => {
      return [name, createErrorType(name, errorTypes[name])];
    })
  );
}

const genericMessages = {
  500: 'Our computers ran into a problem.',
  502: 'Our computers are temporarily unavailable.',
  503: 'Our computers are temporarily unavailable.',
  504: 'Our computers took too long.',
};

const helpLinks = {
  [resourceNames.dropdown]:
    '/help/troubleshoot/behavior/dropdown-menu-in-zap-field-is-empty-or-missing-options',
};

const tryJSON = response => {
  try {
    return JSON.parse(response.responseText);
  } catch (e) {
    return undefined;
  }
};

const getErrorDataFromResponse = response => ({
  status: response.status,
  statusText: response.statusText,
  responseText: response.responseText,
  json: tryJSON(response),
  isNetworkError: !!response.networkErrorMessage,
});

const createActionError = message => ({
  assign({ action }) {
    return {
      action,
    };
  },
  summary(error) {
    return `${message}: ${JSON.stringify(error.action)}`;
  },
});

const createCustomError = (spec = {}) => ({
  assign(detail) {
    if (detail instanceof Error) {
      if (!detail.summary) {
        return {
          summary: detail.message,
        };
      }
    }

    if (_.isObject(detail)) {
      return {
        // If there's no summary and an error was passed, make the error message the summary
        ...(detail instanceof Error && !detail.summary
          ? { summary: detail.message }
          : {}),
        // If an error response was passed in, extract some error info from it
        ...(detail.response ? getErrorDataFromResponse(detail.response) : {}),
        ...detail,
      };
    }

    return {};
  },
  ...spec,
});

// TODO(vitorbal): most of the behavior used for `createDataError` should actually
// be extracted out and re-used by all error types that are consuming a fetch error response.
//
// Right now we have a bit of a mess where sometimes one error "type" actually contains another
// nested error type and this is very confusing.
const createDataError = verb => ({
  assign({ response, resource, nodeId }) {
    return {
      ...getErrorDataFromResponse(response),
      resource,
      nodeId,
      rootErrorMessage: response.networkErrorMessage,
    };
  },
  summary(error) {
    const { resource } = error;
    const resourceName = _.isString(resource) ? resource : 'item';
    if (error.status === 404) {
      return `${resourceName} not found.`;
    }
    const rootErrorMessage =
      error.rootErrorMessage || genericMessages[error.status] || '';
    const rootErrorSuffix = rootErrorMessage ? `: ${rootErrorMessage}` : '.';
    return `Problem ${verb} ${resourceName}${rootErrorSuffix}`;
  },
  helpLink(error) {
    const { resource } = error;
    return helpLinks[resource];
  },
});

export default createErrorTypes({
  DataGetError: createDataError('retrieving'),

  DataPostError: createDataError('creating'),

  DataPatchError: createDataError('updating'),

  DataPutError: createDataError('updating'),

  DataDeleteError: createDataError('removing'),

  UnpauseError: {
    assign({ flowId, response }) {
      return {
        flowId,
        errors: response.errors,
        json: tryJSON(response),
        status: response.status,
        statusText: response.statusText,
        responseText: response.responseText,
      };
    },
    summary() {
      return 'There was a problem activating your Zap.';
    },
  },

  PauseError: {
    assign({ flowId, response }) {
      return {
        flowId,
        errors: response.errors,
        json: tryJSON(response),
        status: response.status,
        statusText: response.statusText,
        responseText: response.responseText,
      };
    },
    summary() {
      return 'There was a problem deactivating your Zap.';
    },
  },

  ActionNotObjectError: createActionError('Action was not an object'),

  ActionTypeNotValid: createActionError('Action type is not valid'),

  SharedZapParamError: {
    assign(detail) {
      return {
        sharedZap: detail.sharedZap || {},
        stepIndex: detail.stepIndex || -1,
        param: detail.param || {},
      };
    },

    summary(error) {
      return `Problem with param {{${error.param.id}__${error.param.key}}} of step ${error.stepIndex} of Shared Zap ${error.sharedZap.id}.`;
    },
  },

  UnknownError: createCustomError(),
  // We need this as an "alias", since the error name is visible
  PlatformError: createCustomError(),

  SignUpValidationError: {
    assign(details) {
      const fieldErrorsError = selectErrorOfType(
        'field_errors',
        details.errors
      );
      const fields = _.get(fieldErrorsError, 'fields', []);
      const fieldErrors = _.fromPairs(
        fields.map(field => [field.key, field.message])
      );
      return {
        ...details,
        fieldErrors,
      };
    },
    summary(error) {
      return (
        'Sign up problems with ' + Object.keys(error.fieldErrors).join(', ')
      );
    },
  },

  SignUpError: {
    assign(details) {
      return details;
    },
    summary() {
      return 'There was a problem while trying to sign up.';
    },
  },

  LoadItemsError: {
    assign({ resource, itemName, nodeId }) {
      return { resource, itemName, nodeId };
    },
    summary(error) {
      const { itemName } = error;
      return `We’re having trouble loading ‘${itemName}’ data`;
    },
    helpLink(error) {
      const { resource } = error;
      return helpLinks[resource];
    },
  },

  LoadItemsNetworkError: {
    assign({ resource, itemName, nodeId, response = {} }) {
      return {
        resource,
        itemName,
        status: response.status,
        statusText: response.statusText,
        responseText: response.responseText,
        nodeId,
      };
    },
    summary(error) {
      const { itemName } = error;
      return `We’re having trouble loading '${itemName}' data`;
    },
    helpLink(error) {
      const { resource } = error;
      return helpLinks[resource];
    },
  },

  SampleError: {
    assign({ id, title, code, status, step }) {
      return {
        id,
        title,
        code,
        status,
        step,
      };
    },
    summary() {
      return 'We had trouble finding a sample.';
    },
    errors({ title }) {
      return [title];
    },
  },
});
